import { request } from "../../backend";
import { AppConfig } from "../../config";
import { Agreement, CreateAgreementParams } from "../../models/Agreement";
import OrderPayload from "../../models/OrderPayload";
import Product from "../../models/Product";

/**
 * Fetches SaleSys stock items.
 */
export async function fetchProducts(): Promise<Product[]> {
  const products = await request('/products-v1', 'get', null, { categoryIds: ["691634afc35bf51ac108d8c3"] }, { api: 'products' });

  const productsWithMonthlyPayment = products?.map(product => {
    return {
      ...product,
      monthlyPayment: Math.round(Math.floor(product.price / 24))
    }
  });

  return productsWithMonthlyPayment || [];
}

/**
 * Creates one SaleSys order per subscription.
 * 
 * Will *not* attach the agreement if `agreementLocation` is set.
 * Use `uploadOrderFile` to attach a file to an order.
 */
export async function createOrder(order: OrderPayload, config: AppConfig, phoneNumber?: string): Promise<{
  orderId: string;
  serialId: number;
  fileToken: string;
}> {
  const body = order.toJson(config, phoneNumber);

  const { orderId, serialId, fileToken } = await request('/orders-v1', 'post', body, null, {
    api: 'orders'
  });

  return { orderId, serialId, fileToken };
}

type NewOrderFile = {
  fileUrl?: string;
  file?: Blob;
  fileName: string;
}

/**
 * Uploads a file to a SaleSys order.
 * 
 * Note that a file URL should be provided in place of a `File` instance.
 * This is due to the proxy downloading and uploading the file, preventing passing a
 * file between two servers.
 */
export async function uploadOrderFile(orderId: string, fileToken: string, file: NewOrderFile): Promise<{
  fileId: string;
}> {
  let body: any = { ...file };

  if (file.file) {
    body = new FormData();
    body.append('json', JSON.stringify({ ...file, file: null }));
    body.append('file', file.file);
  }

  const result = await request(`/orders-v1/${orderId}/files`, 'post', body, null, {
    api: 'orders',
    headers: {
      'x-file-token': fileToken
    }
  });

  return { fileId: result.file?.id ?? null };
}

/**
 * Attaches the QC recording, which has previously been uploaded, to the order.
 */
export async function attachQcRecording(agreementKey: string, orderId?: string, fileToken?: string) {
  await request(`/agreements/${agreementKey}/attach/qc`, 'post', { orderId }, {}, {
    headers: {
      'x-file-token': fileToken
    }
  });
}

/**
 * Uploads the QC recording file so that it can later be attached to the order via backend.
 */
export async function uploadQcRecording(agreementKey: string, file: File) {
  const body = new FormData();
  body.append('qc', file);

  await request(`/agreement/${agreementKey}/qc`, 'post', body, null);
}

/**
 * Creates and uploads an agreement to be signed later.
 */
export async function createUnsignedAgreement(params: CreateAgreementParams): Promise<Agreement> {
  const formData = new FormData();
  formData.append('json', JSON.stringify({
    ...params,
    upload: true
  }));

  if (params.photo) {
    formData.append('photo', params.photo);
  }

  const { key, uri } = await request(`/agreement-preview`, 'post', formData);

  return new Agreement({ key, uri });
}

export async function updateAgreementMetadata(agreementKey: string, update: {
  orderId?: string;
  orderSerialId?: number;
  signCity?: string;
  date?: Date;
}) {
  await request(`/agreement/${agreementKey}/meta`, 'put', update);
}

export type EnergyAgreementAdditionalDocuments = {
  offerUri: string;
};

type UpdateAgreementWithNewCityParams = {
  offerSignatureUri: string;
}

export async function updateAgreementWithNewCityAndDate(agreementKey: string, params: UpdateAgreementWithNewCityParams) {
  const formData = new FormData();
  formData.append("json", JSON.stringify(params));
  return await request(`agreements/${agreementKey}`, "put", formData)
}